import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyDashboard } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  AlertCircle,
  Building2,
  FileCheck2,
  Inbox,
  ReceiptText,
  TrendingUp,
  Users,
} from 'lucide-react';
import {
  EmptyState,
  KpiCard,
  StatusBadge,
  displayAgencyName,
  formatDate,
  formatMoney,
  fullName,
  safeList,
  statusLabel,
} from './AgencyPortalShared';

const pipelineOrder = ['applied', 'under_review', 'offered', 'enrolled', 'waitlisted', 'rejected'];

export default function AgencyDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = await getMyDashboard();
        if (!cancelled) setData(res.data || null);
      } catch (err) {
        console.error(err);
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const pipeline = useMemo(() => data?.pipeline || {}, [data?.pipeline]);
  const pipelineRows = useMemo(() => {
    const known = pipelineOrder.filter((key) => pipeline[key]);
    const extra = Object.keys(pipeline).filter((key) => !pipelineOrder.includes(key));
    return [...known, ...extra].map((key) => ({ key, label: statusLabel(key), value: pipeline[key] || 0 }));
  }, [pipeline]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {[1, 2, 3, 4].map((item) => (
            <div key={item} className="h-28 animate-pulse rounded-xl bg-white" />
          ))}
        </div>
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <div className="h-80 animate-pulse rounded-xl bg-white xl:col-span-2" />
          <div className="h-80 animate-pulse rounded-xl bg-white" />
        </div>
      </div>
    );
  }

  if (!data?.agency) {
    return (
      <div className="space-y-6 animate-fade-in">
        <EmptyState icon={Building2} title="Agency profile not linked">
          Your account is signed in, but an agency profile has not been connected yet.
        </EmptyState>
      </div>
    );
  }

  const agency = data.agency;
  const counts = data.counts || {};
  const missingFields = safeList(data.missing_profile_fields);
  const missingDocuments = safeList(data.missing_documents);
  const missingTotal = missingFields.length + missingDocuments.length;
  const dashboardLayout = data.dashboard_layout || { sections: [], tables: {} };
  const sectionSettings = Object.fromEntries(
    safeList(dashboardLayout.sections).map((section, index) => [section.id, { ...section, order: index }]),
  );
  const section = (id, fallbackTitle) => ({
    title: sectionSettings[id]?.title || fallbackTitle,
    visible: sectionSettings[id]?.visible !== false,
    order: sectionSettings[id]?.order ?? 99,
  });
  const tableColumns = (id) => safeList(dashboardLayout.tables?.[id]).filter((column) => column.visible);
  const kpiSection = section('kpis', 'Overview cards');
  const attentionSection = section('attention', 'Profile attention');
  const referralsSection = section('referrals', 'Recent referrals and pipeline');
  const studentsSection = section('students', 'Linked students');
  const commissionsSection = section('commissions', 'Commission payments');
  const documentsSection = section('documents', 'Documents');
  const referralColumns = tableColumns('referrals');
  const studentColumns = tableColumns('students');
  const commissionColumns = tableColumns('commissions');

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
        <div>
          <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            Welcome, {displayAgencyName(agency)}
          </h2>
          <p className="mt-1 text-sm text-[#062B1A]/60">
            {agency.status ? `${statusLabel(agency.status)} partner workspace` : 'Partner workspace'}
          </p>
        </div>
        <Button onClick={() => navigate('/agency/profile')} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
          <Building2 className="mr-2 h-4 w-4" />
          My Profile
        </Button>
      </div>

      <div className="flex flex-col gap-6">
      {kpiSection.visible && <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4" style={{ order: kpiSection.order }}>
        <KpiCard label="Linked Students" value={counts.linked_students || 0} icon={Users} />
        <KpiCard label="Active Referrals" value={counts.active_applications || 0} icon={Inbox} accent />
        <KpiCard label="Offers" value={counts.offered_applications || 0} icon={TrendingUp} />
        <KpiCard label="Commission Paid" value={formatMoney(data.commission_summary?.total_paid)} icon={ReceiptText} accent />
      </div>}

      {attentionSection.visible && missingTotal > 0 && (
        <Card className="border border-amber-200 bg-amber-50/70 shadow-sm" style={{ order: attentionSection.order }}>
          <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
            <div className="flex gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />
              <div>
                <p className="text-sm font-semibold text-amber-900">Profile items need attention</p>
                <p className="mt-1 text-sm text-amber-800/80">
                  {[...missingFields, ...missingDocuments].slice(0, 4).join(', ')}
                  {missingTotal > 4 ? ` and ${missingTotal - 4} more` : ''}
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate('/agency/documents')} className="border-amber-300 text-amber-900 hover:bg-amber-100">
              Review
            </Button>
          </CardContent>
        </Card>
      )}

      {referralsSection.visible && <div className="grid grid-cols-1 gap-6 xl:grid-cols-3" style={{ order: referralsSection.order }}>
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm xl:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              {referralsSection.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {safeList(data.applications).length === 0 ? (
              <EmptyState icon={Inbox} title="No referrals yet" />
            ) : (
              <>
              <div
                className="grid gap-3 px-3 text-[11px] font-semibold uppercase tracking-wide text-[#062B1A]/45"
                style={{ gridTemplateColumns: `repeat(${Math.max(1, referralColumns.length)}, minmax(0, 1fr))` }}
              >
                {referralColumns.map((column) => <span key={column.id}>{column.label}</span>)}
              </div>
              {safeList(data.applications).map((application) => (
                <button
                  key={application.id}
                  type="button"
                  onClick={() => navigate('/agency/referrals')}
                  className="grid w-full items-center gap-3 rounded-lg bg-[#F7F8FA] p-3 text-left transition-colors hover:bg-[rgba(212,175,55,0.08)]"
                  style={{ gridTemplateColumns: `repeat(${Math.max(1, referralColumns.length)}, minmax(0, 1fr))` }}
                >
                  {referralColumns.map((column) => (
                    <div key={column.id} className="min-w-0">
                      {column.id === 'name' && <p className="truncate text-sm font-semibold text-[#062B1A]">{fullName(application)}</p>}
                      {column.id === 'program' && <p className="truncate text-sm text-[#062B1A]/65">{application.program || 'Program not set'}</p>}
                      {column.id === 'status' && <StatusBadge status={application.status} />}
                    </div>
                  ))}
                </button>
              ))}
              </>
            )}
          </CardContent>
        </Card>

        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              Referral Pipeline
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {pipelineRows.length === 0 ? (
              <EmptyState icon={TrendingUp} title="No pipeline activity" />
            ) : (
              pipelineRows.map((row) => {
                const total = Math.max(1, counts.applications_total || 0);
                return (
                  <div key={row.key}>
                    <div className="mb-1.5 flex items-center justify-between text-sm">
                      <span className="font-medium text-[#062B1A]">{row.label}</span>
                      <span className="text-[#062B1A]/55">{row.value}</span>
                    </div>
                    <Progress value={(row.value / total) * 100} className="h-2 bg-[#062B1A]/10 [&>div]:bg-[#D4AF37]" />
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      </div>}

      {(studentsSection.visible || commissionsSection.visible) && <div
        className="grid grid-cols-1 gap-6 xl:grid-cols-2"
        style={{ order: Math.min(studentsSection.order, commissionsSection.order) }}
      >
        {studentsSection.visible && <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              {studentsSection.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {safeList(data.students).length === 0 ? (
              <EmptyState icon={Users} title="No linked students" />
            ) : (
              <>
                <div
                  className="grid gap-3 px-3 text-[11px] font-semibold uppercase tracking-wide text-[#062B1A]/45"
                  style={{ gridTemplateColumns: `repeat(${Math.max(1, studentColumns.length)}, minmax(0, 1fr))` }}
                >
                  {studentColumns.map((column) => <span key={column.id}>{column.label}</span>)}
                </div>
                {safeList(data.students).map((student) => (
                  <div
                    key={student.id}
                    className="grid items-center gap-3 rounded-lg border border-[rgba(6,43,26,0.08)] p-3"
                    style={{ gridTemplateColumns: `repeat(${Math.max(1, studentColumns.length)}, minmax(0, 1fr))` }}
                  >
                    {studentColumns.map((column) => (
                      <div key={column.id} className="min-w-0">
                        {column.id === 'name' && <p className="truncate text-sm font-semibold text-[#062B1A]">{fullName(student)}</p>}
                        {column.id === 'student_number' && <p className="truncate text-sm text-[#062B1A]/65">{student.student_number || 'Not assigned'}</p>}
                        {column.id === 'program' && <p className="truncate text-sm text-[#062B1A]/65">{student.program_name || 'Program not set'}</p>}
                        {column.id === 'status' && <StatusBadge status={student.enrollment_status || 'student'} />}
                      </div>
                    ))}
                  </div>
                ))}
              </>
            )}
          </CardContent>
        </Card>}

        {commissionsSection.visible && <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              {commissionsSection.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {safeList(data.recent_commission_payments).length === 0 ? (
              <EmptyState icon={ReceiptText} title="No commission payments" />
            ) : (
              <>
                <div
                  className="grid gap-3 px-3 text-[11px] font-semibold uppercase tracking-wide text-[#062B1A]/45"
                  style={{ gridTemplateColumns: `repeat(${Math.max(1, commissionColumns.length)}, minmax(0, 1fr))` }}
                >
                  {commissionColumns.map((column) => <span key={column.id}>{column.label}</span>)}
                </div>
                {safeList(data.recent_commission_payments).map((payment) => (
                  <div
                    key={payment.id}
                    className="grid items-center gap-3 rounded-lg bg-[#F7F8FA] p-3"
                    style={{ gridTemplateColumns: `repeat(${Math.max(1, commissionColumns.length)}, minmax(0, 1fr))` }}
                  >
                    {commissionColumns.map((column) => (
                      <div key={column.id} className="min-w-0">
                        {column.id === 'student' && <p className="truncate text-sm font-semibold text-[#062B1A]">{payment.student_name || 'Commission payment'}</p>}
                        {column.id === 'date' && <p className="text-sm text-[#062B1A]/65">{formatDate(payment.payment_date)}</p>}
                        {column.id === 'amount' && <p className="text-sm font-semibold text-[#062B1A]">{formatMoney(payment.amount)}</p>}
                      </div>
                    ))}
                  </div>
                ))}
              </>
            )}
          </CardContent>
        </Card>}
      </div>}

      {documentsSection.visible && <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm" style={{ order: documentsSection.order }}>
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <FileCheck2 className="h-5 w-5 text-[#D4AF37]" />
            <div>
              <p className="text-sm font-semibold text-[#062B1A]">{documentsSection.title}</p>
              <p className="text-sm text-[#062B1A]/55">
                {missingDocuments.length ? `${missingDocuments.length} document item pending` : 'Document checklist is current'}
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate('/agency/documents')} className="border-[rgba(6,43,26,0.16)] text-[#062B1A] hover:bg-[#F7F8FA]">
            Open Documents
          </Button>
        </CardContent>
      </Card>}
      </div>
    </div>
  );
}
